import {
  decimal,
  invertPrice,
  pairLabel,
  quoteOrder,
  unitLabel,
  type ExitPolicy,
  type OpeningSetup,
  type RangeFrame,
  type RangeOrientations,
  type Token,
} from "@auto-pools/core";
import { shorten } from "./format";

/*
 * The only web file allowed to pick a price orientation. Two source frames exist and every member
 * is told which one it is fed:
 * - canonical (token B per token A): positions, exit policies, close triggers, pool prices;
 * - setup (`OpeningSetup.inverted`): everything inside `OpeningSetup`, which the server already
 *   mapped into the frame the operator opened in.
 * Re-orienting a setup value as if it were canonical inverts it twice.
 */

type Named = Pick<Token, "mint" | "symbol">;
interface Triggers {
  lowerTrigger?: string;
  upperTrigger?: string;
}
interface Stops {
  lowerStop?: string;
  upperStop?: string;
}

export interface PriceFrame {
  /** Display = token A per token B when true. */
  inverted: boolean;
  /** Display order. */
  base: Token;
  quote: Token;
  /** "BASE / QUOTE". */
  pair: string;
  /** "QUOTE per BASE" in this frame. */
  unit: string;
  /** "B per A" — for basis notes only. */
  canonicalUnit: string;
  /** A canonical price in this frame. Throws on a non-positive input when inverted. */
  price(canonical: string): string;
  /** Like `price`, but null on a missing, unparsable or non-positive input; never throws. */
  priceOrNull(canonical: string | null | undefined): string | null;
  /** Canonical bounds in this frame: reciprocal and swapped when inverted. May throw. */
  bounds(lower: string, upper: string): { lower: string; upper: string };
  /**
   * Triggers stated in the frame `sourceInverted` names (canonical by default) as display-side
   * stops. Never throws: an unparsable trigger is dropped.
   */
  stops(t: Triggers, sourceInverted?: boolean): Stops;
  /** A canonical bound or stop side named in this frame. */
  side(canonical: "lower" | "upper"): "lower" | "upper";
  /** Canonical per-side distances as the dialog's display rows; a missing side stays null. */
  distancesToRows(d: { lower: string | null; upper: string | null }): {
    below: string | null;
    above: string | null;
  };
  /** Exact inverse of `distancesToRows`: display rows back to canonical per-side distances. */
  rowsToCanonical(rows: { below: string; above: string }): {
    lowerThresholdPercent: string;
    upperThresholdPercent: string;
  };
  /** The server-stated range frame whose `inverted` matches this one, or the other when flipped. */
  rangeFrame(o: RangeOrientations | null | undefined, flipped?: boolean): RangeFrame | null;
  /** Unit of a frame named by its mints; symbols from this pair's tokens, else a shortened mint. */
  unitOf(baseMint: string, quoteMint: string): string;
  /**
   * A policy re-expressed in THIS frame. `sourceInverted` names the frame the policy is stated in:
   * false (default) for `Position.exitPolicy`, `ClosedPosition.exitSettings`, `closeReason.trigger`;
   * `setup.inverted` for `OpeningSetup.initialExitPolicy`. Identity when the frames agree; otherwise
   * sides, per-side distances and triggers swap, triggers reciprocated. `thresholdPercent`,
   * `thresholdInverted`, `enabled` and `targetMint` are untouched. Never throws: an unparsable
   * trigger is dropped.
   */
  policy(policy: ExitPolicy, sourceInverted?: boolean): ExitPolicy;
  /** Setup values re-expressed in this frame; the policy via `policy(…, setup.inverted)`. May throw. */
  fromSetup(setup: OpeningSetup): {
    referencePrice: string;
    lowerPrice: string;
    upperPrice: string;
    initialExitPolicy: ExitPolicy;
  };
}

function positive(value: string | null | undefined): value is string {
  if (value === null || value === undefined) return false;
  try {
    return decimal(value).units > 0n;
  } catch {
    return false;
  }
}

function inverseOrNull(value: string | null | undefined): string | null {
  return positive(value) ? invertPrice(value) : null;
}

/** Triggers swapped into the other frame; an unparsable or non-positive one is dropped. */
function swapTriggers(t: Triggers): Triggers {
  const lower = inverseOrNull(t.upperTrigger);
  const upper = inverseOrNull(t.lowerTrigger);
  return {
    ...(lower === null ? {} : { lowerTrigger: lower }),
    ...(upper === null ? {} : { upperTrigger: upper }),
  };
}

/** The rule's answer alone — the ONLY call of core `quoteOrder` in apps/web; the wizard's default. */
export function ruleInverted(tokenA: Pick<Token, "mint">, tokenB: Pick<Token, "mint">): boolean {
  return quoteOrder(tokenA.mint, tokenB.mint).inverted;
}

function symbolOf(mint: string, tokens: readonly Named[]): Named {
  return { mint, symbol: tokens.find((token) => token.mint === mint)?.symbol ?? shorten(mint) };
}

export function priceFrame(
  tokenA: Token,
  tokenB: Token,
  inverted = ruleInverted(tokenA, tokenB),
): PriceFrame {
  const toDisplay = (sourceInverted: boolean) => sourceInverted !== inverted;
  const frame: PriceFrame = {
    inverted,
    base: inverted ? tokenB : tokenA,
    quote: inverted ? tokenA : tokenB,
    pair: pairLabel(tokenA, tokenB, inverted),
    unit: unitLabel(tokenA, tokenB, inverted),
    canonicalUnit: unitLabel(tokenA, tokenB, false),
    price: (canonical) => (inverted ? invertPrice(canonical) : canonical),
    priceOrNull: (canonical) => {
      if (!positive(canonical)) return null;
      return inverted ? invertPrice(canonical) : canonical;
    },
    bounds: (lower, upper) =>
      inverted ? { lower: invertPrice(upper), upper: invertPrice(lower) } : { lower, upper },
    stops: (t, sourceInverted = false) => {
      const shown = toDisplay(sourceInverted) ? swapTriggers(t) : t;
      return {
        ...(shown.lowerTrigger === undefined ? {} : { lowerStop: shown.lowerTrigger }),
        ...(shown.upperTrigger === undefined ? {} : { upperStop: shown.upperTrigger }),
      };
    },
    side: (canonical) => (inverted ? (canonical === "lower" ? "upper" : "lower") : canonical),
    // Same mapping as `mapStopPrefill` (below = inverted ? upper : lower), without its filling of
    // a missing legacy side: change detection must see that side as missing.
    distancesToRows: (d) => ({
      below: inverted ? d.upper : d.lower,
      above: inverted ? d.lower : d.upper,
    }),
    rowsToCanonical: (rows) => ({
      lowerThresholdPercent: inverted ? rows.above : rows.below,
      upperThresholdPercent: inverted ? rows.below : rows.above,
    }),
    rangeFrame: (o, flipped = false) => {
      if (!o) return null;
      const want = flipped ? !inverted : inverted;
      return [o.opening, o.flipped].find((candidate) => candidate.inverted === want) ?? null;
    },
    unitOf: (baseMint, quoteMint) =>
      unitLabel(symbolOf(baseMint, [tokenA, tokenB]), symbolOf(quoteMint, [tokenA, tokenB]), false),
    policy: (policy, sourceInverted = false) => {
      if (!toDisplay(sourceInverted)) return policy;
      const { lowerThresholdPercent, upperThresholdPercent, lowerTrigger, upperTrigger, ...rest } =
        policy;
      return {
        ...rest,
        side: policy.side === "below" ? "above" : policy.side === "above" ? "below" : policy.side,
        ...(upperThresholdPercent === undefined
          ? {}
          : { lowerThresholdPercent: upperThresholdPercent }),
        ...(lowerThresholdPercent === undefined
          ? {}
          : { upperThresholdPercent: lowerThresholdPercent }),
        ...swapTriggers({
          ...(lowerTrigger === undefined ? {} : { lowerTrigger }),
          ...(upperTrigger === undefined ? {} : { upperTrigger }),
        }),
      };
    },
    fromSetup: (setup) => {
      const initialExitPolicy = frame.policy(setup.initialExitPolicy, setup.inverted);
      if (!toDisplay(setup.inverted))
        return {
          referencePrice: setup.referencePrice,
          lowerPrice: setup.lowerPrice,
          upperPrice: setup.upperPrice,
          initialExitPolicy,
        };
      return {
        referencePrice: invertPrice(setup.referencePrice),
        lowerPrice: invertPrice(setup.upperPrice),
        upperPrice: invertPrice(setup.lowerPrice),
        initialExitPolicy,
      };
    },
  };
  return frame;
}

/** True when a self-described frame's base is the rule's quote, i.e. it must be reciprocated. */
function againstRule(baseMint: string, quoteMint: string): boolean {
  return ruleInverted({ mint: baseMint }, { mint: quoteMint });
}

/**
 * A self-describing price re-oriented to the rule frame, with its unit; tokens supply symbols.
 * Null on a missing, unparsable or non-positive price; never throws.
 */
export function orientedPrice(
  p: { price: string; baseMint: string; quoteMint: string },
  tokens: readonly Named[],
): { price: string; unit: string } | null {
  if (!positive(p.price)) return null;
  const flip = againstRule(p.baseMint, p.quoteMint);
  const [baseMint, quoteMint] = flip ? [p.quoteMint, p.baseMint] : [p.baseMint, p.quoteMint];
  return {
    price: flip ? invertPrice(p.price) : p.price,
    unit: unitLabel(symbolOf(baseMint, tokens), symbolOf(quoteMint, tokens), false),
  };
}

/**
 * Bounds stated in a self-described frame, re-oriented to the rule frame (reciprocal and swapped
 * when needed). Null when either bound is missing, unparsable or non-positive; never throws.
 */
export function orientedBounds(
  b: { lower: string; upper: string; baseMint: string; quoteMint: string },
  tokens: readonly Named[],
): { lower: string; upper: string; unit: string } | null {
  if (!positive(b.lower) || !positive(b.upper)) return null;
  const flip = againstRule(b.baseMint, b.quoteMint);
  const [baseMint, quoteMint] = flip ? [b.quoteMint, b.baseMint] : [b.baseMint, b.quoteMint];
  return {
    lower: flip ? invertPrice(b.upper) : b.lower,
    upper: flip ? invertPrice(b.lower) : b.upper,
    unit: unitLabel(symbolOf(baseMint, tokens), symbolOf(quoteMint, tokens), false),
  };
}
